// Which screen is on, and how it changes.
//
// THE ADDRESS BAR IS THE ANSWER, not a `useState` beside it. The server already
// treats every unknown non-API path as a client route and hands back
// `index.html` for it (`api/src/http/error-handler.ts`), so the paths in
// `screens.ts` are reachable, linkable and bookmarkable today; a screen state
// that did not write them would leave the browser's own Back button walking out
// of the application, which on a phone is the button people use.
//
// It is the History API directly rather than a router package. The dependency
// budget is deliberately small (web-architecture.md section 4.2 - no ORM, no
// date library, no Redis client), and what a router buys - nested routes, route
// parameters, data loaders - is not what four flat screens need. Should a
// screen ever want a parameter in its path, this is the file that grows one.

import { useCallback, useEffect, useRef, useState } from 'react';
import { HOME, pathOf, screenAt } from './screens.ts';
import type { ScreenName } from './screens.ts';

export type ExternalNavigation = { kind: 'history' } | { kind: 'notification' };

export interface ShellHost {
  /**
   * The browser or service worker moved the application from outside its own
   * controls, so App can apply the shared sheet-navigation behavior.
   *
   * Ordinary `go()` calls stay silent: the navigation's add action goes to
   * Today and opens a sheet in the SAME event (`App.tsx`), so treating every
   * programmatic move as external would close the sheet it had just opened.
   */
  onExternalNavigation: (navigation: ExternalNavigation) => void;
}

export interface Shell {
  screen: ScreenName;
  /** Go to a screen, and say so in the address bar. */
  go: (screen: ScreenName) => void;
  /**
   * Out of a screen that is not a tab, back to the one it was opened from.
   *
   * Deliberately NOT `history.back()`. Settings is linkable, so it can be the
   * first entry in this tab's history - and `history.back()` there leaves
   * Perfect Note altogether, which is the one thing a Back control inside an
   * application must never do. It goes to where this session opened it from,
   * and to Today when this session did not open it at all.
   *
   * It UNWINDS the trail rather than swapping two screens: see `trail` below.
   */
  back: () => void;
  /** The same, with a screen-specific fallback instead of Today. */
  backTo: (fallback: ScreenName) => void;
}

/**
 * How far back an in-app Back control can walk before it bottoms out at Today.
 *
 * A bound rather than none, because a tab press pushes too and somebody can
 * press tabs all day; the trail is a way out of pushed screens, not a record of
 * the session.
 */
const TRAIL_DEPTH = 10;

/**
 * What the service worker sends a tab it is about to focus, and the client half
 * of a protocol whose other half is one line of `web/public/sw.js`.
 *
 * IT IS A MESSAGE RATHER THAN A NAVIGATION, deliberately. The worker could call
 * `WindowClient.navigate()` and be done, but that destroys the document - and
 * the note editor's save is a debounce plus a flush fired from React's unmount
 * teardown, which a torn-down document does not reliably run and whose request
 * is routinely cancelled when it does. Answering here instead moves the shell
 * the ordinary way, so nothing is unloaded and `go` pushes a real history entry:
 * Back returns the person to the screen the notification took them off.
 *
 * The string is written in both files because a worker in `public/` cannot
 * import - it is copied byte for byte and has no module graph. It names no
 * screen: which screen a tap opens is `HOME`, and `screens.ts` is still the one
 * place that is decided.
 */
export const SHOW_TODAY_MESSAGE = 'perfect-note:show-today';
export const SHOW_TODAY_ACK = 'perfect-note:show-today:ack';

export function useShell(host: ShellHost): Shell {
  const [screen, setScreen] = useState<ScreenName>(() => screenAt(location.pathname) ?? HOME);

  const current = useRef<ScreenName>(screen);

  /**
   * THE SCREENS THIS SESSION WALKED FORWARD THROUGH, newest last, and what a
   * Back control inside a screen unwinds. Refs rather than state: nothing draws
   * it, and reading it inside `go` is what keeps `go` stable.
   *
   * IT IS A STACK AND MUST STAY ONE. A single "previous" slot works only while
   * every pushed screen is opened from a TAB: the moment one is opened from
   * another pushed screen, Back writes the slot it just read and the two ping
   * pong for ever with no way home. Today -> Settings -> Board -> Back left
   * Settings' own Back pointing at the Board, and the tab bar was the only way
   * out. So `back()` POPS and pushes nothing.
   *
   * That pair is gone - the Board is a tab now (captain, 2026-08-18) and
   * Settings is the only pushed screen left - so no two screens in this build
   * can show the bug. The rule is not retired, it is waiting: Month and
   * Reminders are pushed screens too, and the next Back control added to one
   * lands straight back on this stack. `web/test/shell.test.ts` drives it here
   * rather than through a screen, so the guard does not depend on which screens
   * happen to exist, and a single-slot `previous` still goes red.
   */
  const trail = useRef<ScreenName[]>([]);

  const pushTrail = useCallback((from: ScreenName) => {
    trail.current.push(from);
    if (trail.current.length > TRAIL_DEPTH) trail.current.shift();
  }, []);

  // Read through a ref so the listener below is registered once: the caller's
  // callback is a fresh closure every render, and depending on it would take the
  // listener off the window and put it back on each time anything re-renders.
  const external = useRef(host.onExternalNavigation);
  external.current = host.onExternalNavigation;

  useEffect(() => {
    // A path no screen answers to, corrected once - with `replaceState`, not
    // `pushState`, so pressing Back does not land on the unreadable path again
    // and bounce straight off it.
    if (screenAt(location.pathname) === null) {
      history.replaceState(null, '', pathOf(HOME));
    }
  }, []);

  useEffect(() => {
    const onPop = () => {
      const next = screenAt(location.pathname) ?? HOME;
      // An entry naming the screen already on. Nothing moved, so nothing is
      // told - and the trail keeps pointing at where this screen was opened
      // from rather than at itself, which is what its Back control follows.
      if (next === current.current) return;
      // The browser walked back along the trail, so that step is spent: the
      // screen it just left is not somewhere `next` was opened from. Any other
      // jump - Forward, or a bookmark - is a step forward like any other.
      if (trail.current[trail.current.length - 1] === next) trail.current.pop();
      else pushTrail(current.current);
      // The browser has already moved; this only catches the screen up with it,
      // so it must not push or replace anything of its own.
      current.current = next;
      setScreen(next);
      external.current({ kind: 'history' });
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [pushTrail]);

  const go = useCallback(
    (next: ScreenName) => {
      // Pressing the tab you are already on is not a navigation. Pushing an
      // entry for it would make Back appear to do nothing, which is worse than
      // nothing.
      if (current.current === next) return;
      pushTrail(current.current);
      current.current = next;
      history.pushState(null, '', pathOf(next));
      setScreen(next);
    },
    [pushTrail],
  );

  // THE THIRD WAY THE SCREEN CHANGES, after a press and the browser's own Back.
  // A notification was tapped and the worker has found this tab; see
  // `SHOW_TODAY_MESSAGE`. It goes through `go` like any other navigation, so the
  // trail, the address bar and Back all behave exactly as they do for a tab
  // press - which is the point of answering here rather than being navigated.
  //
  // Guarded rather than assumed: `navigator.serviceWorker` is absent on an
  // insecure origin and in a browser that has none, and this hook is mounted by
  // every screen in the application, not only by the one setting that uses one.
  useEffect(() => {
    const worker: ServiceWorkerContainer | undefined = navigator.serviceWorker;
    if (worker === undefined || typeof worker.addEventListener !== 'function') return;

    const onMessage = (event: MessageEvent<unknown>) => {
      const data = event.data;
      if (typeof data !== 'object' || data === null) return;
      if ((data as { type?: unknown }).type !== SHOW_TODAY_MESSAGE) return;
      external.current({ kind: 'notification' });
      go(HOME);
      event.ports[0]?.postMessage({ type: SHOW_TODAY_ACK });
    };

    worker.addEventListener('message', onMessage);
    return () => worker.removeEventListener('message', onMessage);
  }, [go]);

  const backTo = useCallback((fallback: ScreenName) => {
    // POPS, and deliberately does not go through `go`: `go` pushes the screen
    // it is leaving, which is what turned a Back into a loop.
    const to = trail.current.pop() ?? fallback;
    if (current.current === to) return;
    current.current = to;
    history.pushState(null, '', pathOf(to));
    setScreen(to);
  }, []);

  const back = useCallback(() => backTo(HOME), [backTo]);

  return { screen, go, back, backTo };
}
